import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Statistic, Spin, Divider } from 'antd';
import { 
  DollarOutlined, 
  GlobalOutlined, 
  MedicineBoxOutlined, 
  BankOutlined,
  RiseOutlined,
  TrophyOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import api from '../api';
import { useFilters } from '../contexts/FilterContext';
import { useTheme } from '../contexts/ThemeContext';
import '../responsive.css';

const { Title, Text, Paragraph } = Typography;

interface ExecutiveSummaryProps {
  data: any;
  loading: boolean;
}

interface DashboardStats {
  totalMarketSize: string;
  totalMarketSize2019?: string; // Add 2019 market size for trial
  projection: string;
  globalRegions: number;
  countriesCovered: number;
  pipelineDrugs: number;
  activeCompanies: number;
  globalCAGR: string;
  topCountries: string[];
  topDrugClass: string;
  baseYear: number;
}

const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({ data, loading }) => {
  const { isDark } = useTheme();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const { filters } = useFilters();

  const fetchStats = async (therapeuticArea: string) => {
    if (!therapeuticArea) return;
    try {
      setChartLoading(true);
      const res = await api.get(`/api/dashboard/summary-stats`, {
        params: { therapeutic_area: therapeuticArea }
      });
      
      setStats(res.data);
    } catch (e) {
      console.error('Failed to load stats', e);
      setStats(null); // Clear stats on error
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(filters.therapeuticArea);
  }, [filters.therapeuticArea]);

  if (chartLoading || !stats) {
    return <Spin size="large" style={{ display: 'block', margin: 'auto' }} />;
  }

  const kpiCards = [
    {
      title: `Total Market Size (${stats.baseYear})`,
      value: stats.totalMarketSize,
      icon: <DollarOutlined />,
      color: '#1890ff',
      description: 'Current market valuation'
    },
    {
      title: `${filters.therapeuticArea === 'Psoriasis - Focus' || filters.therapeuticArea === 'Psoriasis' ? '2035' : 
               filters.therapeuticArea === 'Skin Cancer - Trial Version' ? '2025' : 
               ['Ophthalmology', 'Ophthalmology - In Sight', 'Retinal Disorders', 'Retinal - Focus', 'Glaucoma', 'Glaucoma - Focus', 'Cell, Gene & RNA Therapies', 'Cell, Gene & RNA Therapies - In Sight'].includes(filters.therapeuticArea) ? '2036' : 
               '2034'} Projection`,
      value: filters.therapeuticArea === 'Skin Cancer - Trial Version' ? stats.totalMarketSize : stats.projection,
      icon: <RiseOutlined />,
      color: '#52c41a',
      description: 'Forecasted growth'
    },
    {
      title: 'Global Regions',
      value: stats.globalRegions,
      icon: <GlobalOutlined />,
      color: '#722ed1',
      description: 'Geographic coverage'
    },
    {
      title: 'Countries Covered',
      value: stats.countriesCovered,
      icon: <GlobalOutlined />,
      color: '#13c2c2',
      description: 'Market presence'
    },
    {
      title: 'Pipeline Drugs (Active)',
      value: stats.pipelineDrugs,
      icon: <MedicineBoxOutlined />,
      color: '#fa8c16',
      description: 'Development stage'
    },
    {
      title: 'Active Companies',
      value: stats.activeCompanies,
      icon: <BankOutlined />,
      color: '#faad14',
      description: 'Market participants'
    }
  ];

  // For trial version, modify the first two cards
  if (filters.therapeuticArea === 'Skin Cancer - Trial Version' && stats.totalMarketSize2019) {
    kpiCards[0] = {
      title: 'Total Market Size (2019)',
      value: stats.totalMarketSize2019,
      icon: <DollarOutlined />,
      color: '#1890ff',
      description: 'Initial market valuation'
    };
    kpiCards[1] = {
      title: '2025 Projection',
      value: stats.totalMarketSize, // 2025 data is in totalMarketSize
      icon: <RiseOutlined />,
      color: '#52c41a',
      description: 'Forecasted growth'
    };
  }

  return (
    <div style={{ padding: '0' }}>


      {/* KPI Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: '48px' }}>
        {kpiCards.map((kpi, index) => (
          <Col xs={24} sm={12} md={8} lg={8} xl={8} key={index}>
            <Card 
              hoverable
              style={{ 
                textAlign: 'center',
                border: `2px solid ${kpi.color}`,
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ 
                fontSize: '48px', 
                color: kpi.color, 
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                {kpi.icon}
              </div>
              <Statistic
                title={kpi.title}
                value={kpi.value}
                valueStyle={{ 
                  color: kpi.color, 
                  fontSize: '28px',
                  fontWeight: 'bold'
                }}
              />
              <Text style={{ 
                color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#8c8c8c', 
                fontSize: '14px',
                display: 'block',
                marginTop: '8px'
              }}>
                {kpi.description}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Headline Facts Section */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Title level={3} style={{ margin: 0, color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#000000' }}>
              Headline Facts
            </Title>
          </div>
        }
        style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: isDark ? '2px solid #434343' : '2px solid #f0f0f0'
        }}
      >
        <Row gutter={[32, 24]}>
          {/* Global CAGR */}
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '24px', 
                color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#000000', 
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                Global CAGR
              </div>
              <Title level={4} style={{ margin: '8px 0', color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#8c8c8c' }}>
                By {filters.therapeuticArea === 'Skin Cancer - Trial Version' ? '2019-2025' : 
                    ['Ophthalmology', 'Ophthalmology - In Sight', 'Retinal Disorders', 'Retinal - Focus', 'Glaucoma', 'Glaucoma - Focus', 'Cell, Gene & RNA Therapies', 'Cell, Gene & RNA Therapies - In Sight'].includes(filters.therapeuticArea) ? '2026-2031' : 
                    '2025-2030'} Growth
              </Title>
              <div style={{ 
                marginTop: '16px',
                padding: '16px',
                backgroundColor: isDark ? '#1f1f1f' : '#fafafa',
                borderRadius: '8px',
                border: isDark ? '1px solid #434343' : '1px solid #d9d9d9'
              }}>
                <Text strong style={{ 
                  color: '#52c41a',
                  fontSize: '36px',
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  {stats.globalCAGR}
                </Text>
                <Text style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#8c8c8c', fontSize: '14px' }}>
                  {filters.therapeuticArea === 'Skin Cancer - Trial Version' ? '2019-2025' : 
                   ['Ophthalmology', 'Ophthalmology - In Sight', 'Retinal Disorders', 'Retinal - Focus', 'Glaucoma', 'Glaucoma - Focus', 'Cell, Gene & RNA Therapies', 'Cell, Gene & RNA Therapies - In Sight'].includes(filters.therapeuticArea) ? '2026-2031' : 
                   '2025-2030'} Compound Annual Growth Rate
                </Text>
              </div>
            </div>
          </Col>

          {/* Top Countries */}
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '24px', 
                color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#000000', 
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                Top 3 Countries
              </div>
              <Title level={4} style={{ margin: '8px 0', color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#8c8c8c' }}>
                By {stats.baseYear} Revenue Share
              </Title>
              <div style={{ marginTop: '16px' }}>
                {stats.topCountries.map((country, index) => (
                  <div key={index} style={{ 
                    marginBottom: '8px',
                    padding: '8px 16px',
                    backgroundColor: isDark ? '#1f1f1f' : '#fafafa',
                    borderRadius: '6px',
                    border: isDark ? '1px solid #434343' : '1px solid #d9d9d9'
                  }}>
                    <Text strong style={{ 
                      color: '#1890ff',
                      fontSize: '16px'
                    }}>
                      #{index + 1} {country}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          {/* Top Drug Class */}
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: '24px', 
                color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#000000', 
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                Leading Drug Class
              </div>
              <Title level={4} style={{ margin: '8px 0', color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#8c8c8c' }}>
                By {stats.baseYear} Revenue
              </Title>
              <div style={{ 
                marginTop: '16px',
                padding: '16px',
                backgroundColor: isDark ? '#1f1f1f' : '#fafafa',
                borderRadius: '8px',
                border: isDark ? '1px solid #434343' : '1px solid #d9d9d9'
              }}>
                <Text strong style={{ 
                  color: '#1890ff',
                  fontSize: '18px',
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  {stats.topDrugClass}
                </Text>
                <Text style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#8c8c8c', fontSize: '14px' }}>
                  Dominant therapeutic approach in the market
                </Text>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Market Overview Section */}
      <div style={{ marginTop: '48px' }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card title="Market Dynamics" style={{ borderRadius: '8px' }}>
              <Paragraph style={{ fontSize: '16px', lineHeight: '1.6' }}>
                {filters.therapeuticArea === 'Psoriasis - Focus' || filters.therapeuticArea === 'Psoriasis' ? (
                  <>
                    The psoriasis treatment market is driven by the chronic and recurrent nature of the disease, requiring long-term management rather than episodic care. Moderate to severe psoriasis accounts for a disproportionate share of treatment value due to reliance on systemic therapies, particularly biologics and advanced small molecules.
                    <br /><br />
                    The market is characterized by high treatment switching, competitive differentiation across efficacy, safety, and dosing convenience, and ongoing expansion of labeled indications into related immune-mediated conditions. Strong pipeline activity and lifecycle management of established brands continue to shape competitive positioning.
                  </>
                ) : filters.therapeuticArea === 'Dermatology - In Sight' || filters.therapeuticArea === 'Dermatology' ? (
                  <>
                    The global dermatology market is experiencing steady growth, driven by the rising prevalence of chronic skin conditions such as psoriasis, atopic dermatitis, acne, and alopecia. Aging populations, urban lifestyles, and increased awareness of skin health are contributing to sustained demand for dermatologic treatments across medical and aesthetic segments.
                    <br /><br />
                    The market is increasingly shaped by innovation in biologics, targeted therapies, and dermatology devices, supported by active clinical pipelines and expanding treatment indications. Growing specialization within dermatology care and improved access to treatment in emerging markets continue to influence competitive dynamics and long-term market expansion.
                  </>
                ) : filters.therapeuticArea === 'Skin Cancer - Focus' || filters.therapeuticArea === 'Skin Cancer' || filters.therapeuticArea === 'Skin Cancer - Trial Version' ? (
                  <>
                    The skin cancer drugs market is driven by rising global incidence, particularly of melanoma and non-melanoma skin cancers, and by continued advances in immunotherapy and targeted treatments. The market is characterized by high clinical innovation, strong R and D investment, and competitive positioning around survival outcomes and treatment durability.
                  </>
                ) : filters.therapeuticArea === 'Ophthalmology - In Sight' || filters.therapeuticArea === 'Ophthalmology' ? (
                  <>
                    The global ophthalmology market is undergoing a significant transformation driven by an aging population and the increasing prevalence of chronic eye conditions. Market expansion is supported by advancements in diagnostic technologies and a shift toward minimally invasive surgical procedures, alongside a growing emphasis on early intervention to prevent vision loss.
                    <br /><br />
                    The landscape is increasingly shaped by innovation in gene and cell therapies, as well as digital health integration, ensuring more personalized and effective patient management across both medical and surgical segments.
                  </>
                ) : filters.therapeuticArea === 'Retinal - Focus' || filters.therapeuticArea === 'Retinal Disorders' ? (
                  <>
                    The retinal disorders market is dominated by the treatment of Age-related Macular Degeneration (AMD) and diabetic eye diseases. Current trends show a strong shift toward longer-acting anti-VEGF therapies and the emergence of gene therapies for inherited retinal diseases.
                    <br /><br />
                    Competitive dynamics are shaped by the critical need for reduced treatment frequency and improved long-term patient outcomes in chronic retinal care, supported by a robust clinical pipeline targeting both neovascular and degenerative retinal conditions.
                  </>
                ) : filters.therapeuticArea === 'Glaucoma - Focus' || filters.therapeuticArea === 'Glaucoma' ? (
                  <>
                    The glaucoma drugs market is centered on the management of intraocular pressure (IOP) to prevent progressive optic nerve damage and irreversible vision loss. Innovation is focused on novel drug classes, such as ROCK inhibitors and nitric oxide donors, that offer alternative mechanisms to traditional therapies.
                    <br /><br />
                    The market is further evolving through the development of sustained-release drug delivery systems and a paradigm shift toward earlier procedural intervention, addressing long-standing challenges with patient adherence to daily eye drops.
                  </>
                ) : filters.therapeuticArea === 'Cell, Gene & RNA Therapies - In Sight' || filters.therapeuticArea === 'Cell, Gene & RNA Therapies' ? (
                  <>
                    The cell, gene, and RNA therapies market is one of the fastest-growing segments in biopharma, driven by transformational science across four submarket pillars: non-genetically-modified cell therapy, gene-modified (ex vivo) cell therapy, in vivo gene therapy, and RNA & oligonucleotide therapeutics. Together these modalities target diseases that have historically lacked durable disease-modifying options, with oncology, hematology, and rare genetic disorders leading near-term commercialization.
                    <br /><br />
                    Competitive dynamics are shaped by manufacturing complexity (autologous vs allogeneic), delivery innovation (LNP, AAV, GalNAc conjugation), payer scrutiny on durability of effect, and an accelerating wave of regulatory approvals - particularly for CAR-T, AAV gene therapy, and RNA-based platforms. Investment continues to flow into platform companies as more first-in-class approvals expand the addressable patient population.
                  </>
                ) : (
                  <>
                    The market is experiencing robust growth driven by increasing 
                    incidence rates, technological advancements in drug development, and expanding 
                    therapeutic options. The market is characterized by strong R&D investments and 
                    strategic partnerships between pharmaceutical companies.
                  </>
                )}
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Growth Drivers" style={{ borderRadius: '8px' }}>
              <ul style={{ paddingLeft: '20px', fontSize: '16px', lineHeight: '1.6' }}>
                {filters.therapeuticArea === 'Psoriasis - Focus' || filters.therapeuticArea === 'Psoriasis' ? (
                  <>
                    <li>Rising global prevalence of moderate to severe psoriasis</li>
                    <li>Continued uptake of biologics and targeted systemic therapies</li>
                    <li>Innovation in IL-17, IL-23, and oral small-molecule treatments</li>
                    <li>Increased diagnosis rates and earlier escalation to advanced therapies</li>
                    <li>Expansion of reimbursement access across developed and emerging markets</li>
                    <li>Growing physician and patient preference for high-efficacy long-term control</li>
                  </>
                ) : filters.therapeuticArea === 'Dermatology - In Sight' || filters.therapeuticArea === 'Dermatology' ? (
                  <>
                    <li>Increasing global prevalence of chronic and lifestyle-associated skin disorders</li>
                    <li>Strong innovation in biologics, JAK inhibitors, and targeted dermatology therapies</li>
                    <li>Expanding demand for aesthetic and cosmetic dermatology procedures</li>
                    <li>Improved access to dermatology care and specialist services in emerging markets</li>
                    <li>Rising healthcare expenditure and patient willingness to pursue long-term treatment</li>
                    <li>Greater awareness, diagnosis rates, and early intervention across skin conditions</li>
                  </>
                ) : filters.therapeuticArea === 'Skin Cancer - Focus' || filters.therapeuticArea === 'Skin Cancer' || filters.therapeuticArea === 'Skin Cancer - Trial Version' ? (
                  <>
                    <li>Increasing incidence of skin cancers globally</li>
                    <li>Advances in immunotherapy and targeted oncology treatments</li>
                    <li>Expansion of treatment options across disease stages</li>
                    <li>Rising healthcare expenditure and oncology drug adoption</li>
                    <li>Improved screening, diagnosis, and earlier intervention</li>
                  </>
                ) : filters.therapeuticArea === 'Ophthalmology - In Sight' || filters.therapeuticArea === 'Ophthalmology' ? (
                  <>
                    <li>Rising global prevalence of vision impairment and age-related eye diseases</li>
                    <li>Technological advancements in ophthalmic imaging and diagnostic tools</li>
                    <li>Increased adoption of minimally invasive glaucoma and cataract surgeries</li>
                    <li>Growing demand for specialized eye care in emerging economies</li>
                    <li>Innovation in gene and cell therapies for previously untreatable conditions</li>
                    <li>Rising healthcare awareness and regular eye screening programs</li>
                  </>
                ) : filters.therapeuticArea === 'Retinal - Focus' || filters.therapeuticArea === 'Retinal Disorders' ? (
                  <>
                    <li>Increasing incidence of AMD and diabetic retinopathy due to aging populations</li>
                    <li>Development of sustained-release and long-acting anti-VEGF medications</li>
                    <li>Strong pipeline of gene therapies targeting inherited retinal disorders (IRDs)</li>
                    <li>Improved diagnostic capabilities with high-resolution OCT imaging</li>
                    <li>Rising patient pool in emerging markets with increasing access to specialists</li>
                    <li>Growing clinical focus on neuroprotection and dry AMD treatments</li>
                  </>
                ) : filters.therapeuticArea === 'Glaucoma - Focus' || filters.therapeuticArea === 'Glaucoma' ? (
                  <>
                    <li>Aging global population and rising prevalence of open-angle glaucoma</li>
                    <li>Innovation in new drug classes including ROCK inhibitors and nitric oxide donors</li>
                    <li>Development of sustained-release implants to improve medication adherence</li>
                    <li>Paradigm shift toward earlier procedural intervention and MIGS</li>
                    <li>Increased screening and diagnosis rates through advanced imaging</li>
                    <li>Rising demand for fixed-dose combinations to simplify treatment regimens</li>
                  </>
                ) : filters.therapeuticArea === 'Cell, Gene & RNA Therapies - In Sight' || filters.therapeuticArea === 'Cell, Gene & RNA Therapies' ? (
                  <>
                    <li>Accelerating wave of approvals across CAR-T, AAV gene therapy, and RNA platforms</li>
                    <li>Expanding addressable patient populations as therapies move into earlier lines and broader indications</li>
                    <li>Manufacturing innovation reducing cost and complexity for autologous and allogeneic cell therapies</li>
                    <li>Maturing delivery technologies (LNPs, GalNAc conjugates, optimized AAV capsids)</li>
                    <li>Strong venture and large-pharma investment in platform companies</li>
                    <li>Increasing regulatory support for orphan and breakthrough therapy designations</li>
                    <li>Growing real-world evidence supporting durability and long-term clinical benefit</li>
                  </>
                ) : (
                  <>
                    <li>Rising market prevalence globally</li>
                    <li>Advancements in novel therapeutic approaches</li>
                    <li>Increasing healthcare expenditure and specialized care access</li>
                    <li>Growing awareness and early detection programs</li>
                  </>
                )}
              </ul>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ExecutiveSummary;
